const DEFAULT_CHUNK_SIZE = 8 * 1024 * 1024;

/**
 * Class for uploading file to google cloud storage via resumable upload.
 *
 * @example
 *
 *   const upload = new GoogleCloudStorageUpload();
 *   const uploadResult = await upload.createUpload(file, { context: 'mycampaign' });
 *   if (uploadResult) {
 *     upload.upload();
 *   }
 *
 * @see {@link https://cloud.google.com/storage/docs/resumable-uploads}
 */
class GoogleCloudStorageUpload {

  #id = '';

  /**
   * @type {AbortController}
   */
  #abortController = null;
  /**
   * @type {File}
   */
  #file = null;
  /**
   * @type {UploadResult}
   */
  #uploadResult = null;
  #listeners = [];

  constructor() {
    this.#id = 'gcsu-' + crypto.randomUUID();
  }

  /**
   * Get id for this upload instance.
   *
   * @returns {string} Unique id.
   */
  getId() {
    return this.#id;
  }

  /**
   * Get file that will be uploaded.
   *
   * @returns {File}
   */
  getFile() {
    return this.#file;
  }

  /**
   * Get upload result.
   *
   * @returns {UploadResult | null} Result object if {@link startResumableUpload} has been called, null otherwise.
   */
  getUploadResult() {
    return this.#uploadResult;
  }

  addEventListener(name, callback) {
    this.#listeners.push({ name, callback });
  }

  removeEventListener(name, callback) {
    this.#listeners.filter(item => item.name !== name && item.callback !== callback);
  }

  #trigger(name, data = []) {
    this.#listeners.filter(item => item.name === name).forEach(item => {
      item.callback.apply(null, data);
    });
  }

  /**
   * Creates resumable file upload which will enable uploading file data directly to Google Cloud Storage bucket.
   *
   * @param {File} file File to upload.
   * @param {object} options File options.
   * @param {string} options.context Defines context where this file belongs to (eg. campaign id), this is internally mapped to correct bucket and folder.
   * @param {string} [options.name] Preferred file name, this may or may not be used in bucket.
   * @param {boolean} [options.startUpload] Whether data upload is started immediately. If false, upload must be started by calling {@link upload} method.
   * @param {number} [options.chunkSize] Size of single chunk in bytes. Defaults to 4MB.
   * @return {Promise<UploadResult>} Upload result object or error if upload could not be created.
   */
  async createUpload(file, options = {}) {
    if (this.getUploadResult()) {
      throw new Error('Upload session already started');
    }

    const response = await fetch('/api/v1/uploads/upload/', {
      method: 'POST',
      body: JSON.stringify({
        context: options.context,
        name: options.name || file.name || '',
        contentType: file.type,
        contentLength: file.size,
      }),
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-with': 'XMLHttpRequest',
      },
    });
    const body = await response.json();
    if (response.status !== 201) {
      throw new Error(body.message);
    }

    const chunkSize = options.chunkSize || DEFAULT_CHUNK_SIZE;
    this.#file = file;
    this.#setUploadResult({
      ...body,
      resultUrl: null,
      status: 'new',
      bytesUploaded: 0,
      chunkSize,
      progress: 0,
    });
    if (options.startUpload) {
      this.upload();
    }

    return this.getUploadResult();
  }

  #setUploadResult(result = {}) {
    const newResult = {
      ...(this.#uploadResult || {}),
      ...result,
    };
    const oldResult = this.#uploadResult;
    this.#uploadResult = newResult;
    this.#trigger('change', [newResult, oldResult]);

    if (newResult.status !== oldResult?.status) {
      this.#trigger('status', [newResult.status]);
    }

    if (newResult.status === 'uploading' && newResult.progress !== oldResult?.progress) {
      this.#trigger('progress', [newResult.progress]);
    }
  }

  /**
   * Get amount of bytes sent from range header.
   *
   * @param {string} value Header value.
   * @returns {number} Amount of bytes sent.
   */
  #parseRangeHeader(value) {
    const range = value ? value.replace('bytes=', '').trim() : '';
    if (!value || value.includes(',')) {
      throw new Error('Unsupported range value: ' + value);
    }
    const lastByte = parseInt(range.split('-')[1]);
    if (isNaN(lastByte)) {
      throw new Error('Byte amount not defined in range header: ' + value);
    }

    return lastByte === 0 ? 0 : lastByte + 1;
  }

  /**
   * Get upload session status from storage api.
   *
   * @param {string} url Upload url.
   * @param {number} contentLength Total file size.
   * @returns {Promise<object>} Upload status and amount of bytesUploaded if current status is `waiting`.
   */
  async #fetchUploadStatus(url, contentLength) {
    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Length': '0',
          'Content-Range': `bytes */${contentLength}`
        }
      });

      if (response.status === 200 || response.status === 201) {
        return {
          status: 'completed'
        };
      }
      else if (response.status === 308) {
        return {
          status: 'waiting',
          bytesUploaded: this.#parseRangeHeader(response.headers.get('range')),
        };
      }
      else if (response.status >= 500) {
        return {
          status: 'interrupted'
        };
      }
      else {
        return {
          status: 'failed'
        };
      }
    }
    catch (e) {
      return {
        status: 'failed'
      };
    }
  }

  /**
   * Uploads next chunk of data to Google Cloud Storage.
   */
  async #uploadNextChunk() {
    const result = this.getUploadResult();
    if (['error', 'failed', 'completed'].includes(result.status)) {
      return;
    }
    let bytesUploaded = result.bytesUploaded;

    // Since upload was stopped, we should request up-to-date status from server so that we do not accidentally corrupt uploaded file
    if (['paused', 'interrupted'].includes(result.status)) {
      const statusResult = await this.#fetchUploadStatus(result.url, result.contentLength);
      if (statusResult.status === 'waiting') {
        bytesUploaded = statusResult.bytesUploaded;
      }
      else if (statusResult.status === 'completed') {
        this.#setUploadResult({
          bytesUploaded: result.contentLength,
          progress: 1,
          status: 'completed'
        });
        return;
      }
      else {
        this.#setUploadResult({
          status: statusResult.status,
        });
        return;
      }
    }

    const file = this.getFile();
    const start = bytesUploaded;
    const end = Math.min(result.chunkSize + start, file.size);
    const body = file.slice(start, end, file.type);
    this.#abortController = new AbortController();

    this.#setUploadResult({
      status: 'uploading',
    });

    try {
      const headers = {
        'Content-Length': body.size,
      };

      if (result.bytesUploaded > 0 || result.chunkSize < result.contentLength) {
        headers['Content-Range'] = `bytes ${start}-${end - 1}/${file.size}`;
      }

      const response = await fetch(result.url, {
        method: 'PUT',
        headers,
        body,
        signal: this.#abortController.signal,
      });
      //console.log('Upload', response);

      if (response.status === 308) {
        const newBytesUploaded = this.#parseRangeHeader(response.headers.get('range'));
        this.#setUploadResult({
          bytesUploaded: newBytesUploaded,
          progress: newBytesUploaded / result.contentLength
        });
        //console.log(this.getUploadResult());

        if (this.getUploadResult().status === 'uploading') {
          this.#uploadNextChunk();
        }
        this.#abortController = null;
        return;
      }

      const newResult = {};
      if (response.status === 200 || response.status === 201) {
        const responseBody = await response.json();
        newResult.bytesUploaded = result.contentLength;
        newResult.progress = 1;
        newResult.status = 'completed';
        newResult.resultUrl = responseBody.mediaLink;
      }
      else if (response.status >= 500) {
        newResult.status = 'interrupted';
      }
      else {
        newResult.status = 'failed';
      }

      this.#setUploadResult(newResult);
    }
    catch(e) {
      console.error('Upload', e);

      // Error was caused manual call to abort controller, status change is handled by caller
      if (['PAUSED', 'CANCELED'].includes(e?.message || e)) {
        return;
      }

      this.#setUploadResult({
        status: 'failed'
      });
    }
    finally {
      this.#abortController = null;
    }
  }

  /**
   * Continues or starts upload process.
   *
   * @return {boolean} True if upload was started/continued.
   */
  upload() {
    const result = this.getUploadResult();
    if (!result.status || !['new', 'paused', 'interrupted'].includes(result.status)) {
      return false;
    }

    this.#uploadNextChunk();

    return true;
  }

  /**
   * Pauses upload process after current chunk has been uploaded.
   *
   * @return {boolean} True if upload was paused.
   */
  pauseUpload() {
    const result = this.getUploadResult();
    if (!result?.status || !['new', 'uploading'].includes(result.status)) {
      return false;
    }

    if (this.#abortController) {
      this.#abortController.abort('PAUSED');
      this.#abortController = null;
    }
    this.#setUploadResult({
      status: 'paused',
    });

    return true;
  }

  /**
   * Cancels created resumable upload instance.
   *
   * Note! If upload is already completed, this does nothing.
   *
   * @returns {boolean} True if upload was canceled.
   */
  cancelUpload() {
    const result = this.getUploadResult();
    if (!result || result?.status === 'completed') {
      return false;
    }

    if (this.#abortController) {
      this.#abortController.abort('CANCELED');
      this.#abortController = null;
    }
    this.#setUploadResult({
      status: 'canceled',
    });

    // Send request to server that attempts to cancel upload session
    fetch(`/api/v1/uploads/upload/${result.id}/`, {
      method: 'DELETE',
      headers: {
        'X-Requested-with': 'XMLHttpRequest',
      },
    }).then((response) => {
      console.log('Cancel', response);
    }).catch((e) => {
      console.error('Cancel', e);
    });

    return true;
  }

}

/**
 * @export
 * @typedef {object} UploadResult
 * @prop {string} id Upload id.
 * @prop {string} url Upload url.
 * @prop {string|null} resultUrl Url to uploaded file.
 * @prop {string} contentType File content type.
 * @prop {number} contentLength File length in bytes.
 * @prop {string} status Current upload status. One of: `new`, `uploading`, `paused`, `completed`, `canceled`, `failed`, 'interrupted'
 * @prop {number} bytesUploaded Amount of uploaded chunks.
 * @prop {number} chunkSize Maximum amount of bytes sent in one request.
 * @prop {number} progress Upload progress as a float between 0 and 1.
 */

export default GoogleCloudStorageUpload;
